import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { LOGO_MARK } from "./data";

const links = [
  { label: "Meet Your Facilitator", id: "founder" },
  { label: "Services", id: "services" },
  { label: "Approach", id: "approach" },
  { label: "Mission", id: "mission" },
  { label: "Voices", id: "voices" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-bone/85 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="nav-brand"
          className="flex items-center gap-3 shrink-0"
        >
          <img src={LOGO_MARK} alt="The People's Josh logo" className="h-14 md:h-16 w-auto" />
          <span className="hidden sm:flex flex-col leading-tight text-left">
            <span className="text-xl md:text-2xl font-semibold font-serif tracking-normal text-ink">
              The People's{" "}
              <span
                className="relative inline-block group/josh ml-1 font-semibold italic text-terra"
                style={{ fontFamily: "Garamond, 'Cormorant Garamond', serif" }}
                data-testid="nav-josh-word"
              >
                Josh
                <span
                  data-testid="nav-josh-meaning"
                  className="pointer-events-none absolute left-1/2 top-full z-[70] mt-4 w-72 -translate-x-1/2 translate-y-1 bg-ink px-4 py-3 text-left text-sm font-normal not-italic font-sans normal-case leading-relaxed tracking-normal text-bone opacity-0 invisible shadow-xl transition-all duration-300 group-hover/josh:visible group-hover/josh:translate-y-0 group-hover/josh:opacity-100"
                >
                  <span className="block text-terra font-semibold">Josh /dʒoːʃ/ (noun):</span>
                  meaning an inner drive that inspires growth, resilience, and meaningful action.
                </span>
              </span>
              <sup className="text-[0.28em] font-medium align-super font-sans not-italic">TM</sup>
            </span>
            <span className="text-[0.68rem] md:text-xs font-medium text-ink/70 tracking-wide">Education Consultancy</span>
            <span className="text-[0.6rem] md:text-[0.68rem] text-ink/45 tracking-wide">Bringing Evidence-Led Education to You</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-6">
          {links.map((l) =>
            l.id === "services" ? (
              <div key={l.id} className="relative group">
                <button onClick={() => go("services")} data-testid="nav-services" className="text-sm tracking-wide text-ink/70 hover:text-ink transition-colors flex items-center gap-1">
                  {l.label} <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-bone border border-ink/10 shadow-xl min-w-[240px] py-2">
                    <button onClick={() => go("services")} data-testid="nav-services-workshops" className="block w-full text-left px-4 py-2.5 text-sm text-ink/70 hover:text-terra hover:bg-sand/40 transition-colors">Workshops</button>
                    <button onClick={() => go("inclusion-support")} data-testid="nav-services-inclusion" className="block w-full text-left px-4 py-2.5 text-sm text-ink/70 hover:text-terra hover:bg-sand/40 transition-colors">Strategic Inclusion Support</button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-${l.id}`}
                className="text-sm tracking-wide text-ink/70 hover:text-ink transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-terra transition-all duration-300 group-hover:w-full" />
              </button>
            )
          )}
        </div>

        <button
          onClick={() => go("book")}
          data-testid="nav-cta"
          className="bg-terra text-white text-xs md:text-sm px-4 md:px-5 py-2.5 hover:bg-ink transition-colors duration-300"
        >
          Book a Free Consultation
        </button>
      </div>
    </motion.nav>
  );
};
